import { useEffect, useMemo } from "react";
import { useAuth } from "../store/useAuth";
import useCookieImport from "react-use-cookie";
// Workaround: react-use-cookie is CJS-only; Vite 8 may wrap the default export
const useCookie = typeof useCookieImport === "function"
  ? useCookieImport
  : (useCookieImport as unknown as { default: typeof useCookieImport }).default;
import { getMe } from "../api/get";
import type { IUserLoginResponse } from "../types/auth";
import { globalErrorHandler } from "../utils/errorHandler";
import { parseStoredLogin } from "../utils/storedLogin";

export default function AuthProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const logIn = useAuth((state) => state.logIn);
  const logOut = useAuth((state) => state.logOut);
  const [userToken] = useCookie("token", "");

  const user: IUserLoginResponse | null = useMemo(
    () => parseStoredLogin(userToken),
    [userToken]
  );

  useEffect(() => {
    globalErrorHandler.setLogoutHandler(() => {
      logOut();
    });

    if (!user?.token) {
      logOut();
      return;
    }

    // Initialize store with cookie data BEFORE calling getMe
    // so http.ts can access the refresh token for auto-refresh on 401
    useAuth.setState({ user, token: user.token });

    getMe(user.token)
      .then((response) => {
        // getMe succeeded (possibly after an auto-refresh by http.ts)
        // Use the latest token from the store (may have been refreshed)
        const state = useAuth.getState();
        logIn({
          user: response.user,
          token: state.token || user.token,
          refreshToken: state.user?.refreshToken || user.refreshToken,
        });
      })
      .catch(() => {
        // http.ts already tried refresh and failed — logout
        logOut();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
}
